import React, { useCallback, useEffect, useMemo, useState } from "react";
import SocialLinks from "../../components/ViewMode/SocialLinks";
import { getJurisdictionLabel } from "../../utils/jurisdiction/structuredJurisdiction";
import SvgIcon from "../../components/ViewMode/IconComponent";
import { IPerson } from "../../interfaces/models/IPerson";
import type { IAreaOfPractice } from "../../interfaces/models/IAreaOfPractice";
import {
  normalizePersonAreasOfPracticeIds,
  normalizePersonAopExpertiseIds,
} from "../../utils/personAopNormalize";
import { getAOPsByIds } from "../../Services/AreaOfPractice";
import { getPersonLeadership } from "../../Services/PersonLeadership";
import { UsePracticeIndustryList } from "../../hooks/Catalogs/usePracticeIndustry";
import { UseFirmList } from "../../hooks/Catalogs/useFirm";
import { IOfficeNew } from "../../interfaces/models/IOffice";
import { ILeadershipEntry } from "../../interfaces/models/ILeadershipEntry";
import { getBlogsByAuthorName } from "../../Services/Blog";
import { IBlogCard } from "../MyWSG/Cards/Blog";
import { getLanguages } from "../../Services/languge";
import { ILanguage } from "../../interfaces/models/ILanguage";
import ProfileOverviewSection, {
  type ProfileOverviewSectionVariant,
} from "./ProfileOverviewSection";
import { LEGACY_MEMBER_URL } from "../../Enums/LegacyRedirection";
import RedPencil from "../../components/Atomos/Icons/RedPencil";
import Email from "../../components/Atomos/Icons/Email";
import Phone from "../../components/Atomos/Icons/Phone";
import ProfilePhoto from "../../components/Atomos/ProfilePhoto";
import UploadProfileImageModal from "../../components/Moleculas/Profile/UploadProfileImageModal";
import { resolveProfilePictureDisplayUrl } from "../../utils/profilePictureUrl";
import { PROFILE_PHOTO_UPDATED_EVENT } from "../../utils/notifyProfilePhotoUpdated";
import EditSocialMediaModal from "../../components/Moleculas/Profile/EditSocialMediaModal";
import EditAreasOfPracticeModal from "../../components/Moleculas/Profile/EditAreasOfPracticeModal";
import EditGeneralProfileModal from "../../components/Moleculas/Profile/EditGeneralProfileModal";
import EditProfessionalCareerModal from "../../components/Moleculas/Profile/EditProfessionalCareerModal";
import ChevronRight from "../../components/Atomos/Icons/ChevronRight";
import ProfilePreferences from "../../components/Atomos/Icons/ProfilePreferences";
import ProfileListMoreModal from "./ProfileListMoreModal";
import type { PersonSocialNetwork } from "../../interfaces/models/IPersonCreate";
import { AffiliationsEnum } from "../../Enums/PersonEnums";
import {
  formatFirmLocalTimeLine,
  resolveIanaFromFirmCountryCity,
} from "../../utils/firmLocalTime";
import { isPersonAttorney } from "../../utils/firmContactUtils";

/** Member-site ASP tools; base host matches {@link LEGACY_MEMBER_URL} (prod / staging / release). */
const MANAGE_BLOGROLLS_URL = `${LEGACY_MEMBER_URL}/manage_blogrolls.asp`;
const MY_PREFERENCES_URL = "/my-preferences";

const AFFILIATION_ENUM_VALUE_SET = new Set<string>(
  Object.values(AffiliationsEnum),
);

/** Inline card: max visible rows before “View more” opens full list modal. */
const PRACTICE_EXPERTISE_PREVIEW_MAX = 6;
const AREAS_OF_PRACTICE_PREVIEW_MAX = 4;
const WSG_PRACTICE_INDUSTRIES_PREVIEW_MAX = 6;
const WSG_LEADERSHIP_PREVIEW_MAX = 4;

/** Legacy `ul.profile-list.list-unstyled`: one line per row, no bullets. */
const WSG_LEADERSHIP_LIST_CLASS =
  "w-full list-none space-y-1 p-0 text-left text-base sm:text-[17px] 2xl:text-[18px] leading-snug text-gray-font md:text-base";
const WSG_LEADERSHIP_ITEM_CLASS = "pl-0";

/** In-card subsection titles (Roboto; overrides global `h3 { font-decimal }`). */
const PROFILE_CARD_SUBTITLE_ROBOTO =
  "mb-4 text-sm sm:text-[17px] 2xl:text-[18px] font-bold text-gray-font !font-roboto";

/** Bold / headings inside Articles (and similar) HTML content. */
const PROFILE_RICH_TEXT_BOLD_ROBOTO =
  "[&_strong]:!font-roboto [&_b]:!font-roboto [&_h1]:!font-roboto [&_h2]:!font-roboto [&_h3]:!font-roboto [&_h4]:!font-roboto";

function personMayEditAreasOfPractice(p: IPerson): boolean {
  return isPersonAttorney(p);
}

export function displayPersonName(person: IPerson): string {
  const parts = [person.name, person.middleInitial, person.lastName].filter(
    (p) => p && String(p).trim(),
  );
  return parts.join(" ").replace(/\s+/g, " ").trim();
}

export type ProfileOverviewBrand = "member" | "staff";

export function getProfileOverviewPageTitle(isOwnProfile: boolean): string {
  return isOwnProfile ? "My Profile" : "Member Profile";
}

function useProfileBrand(theme: ProfileOverviewBrand) {
  return useMemo(() => {
    const staff = theme === "staff";
    return {
      sectionVariant: (staff
        ? "staff"
        : "member") as ProfileOverviewSectionVariant,
      text: staff ? "text-secondary" : "text-primary",
      textHover: staff
        ? "hover:text-secondary-dark"
        : "hover:text-primary-dark",
      borderLight: staff
        ? "border-accent-Default-light"
        : "border-primary-light",
      ringGroup: staff
        ? "hover:ring-secondary/30 focus-visible:ring-secondary"
        : "hover:ring-primary/30 focus-visible:ring-primary",
      pencilOverlayBg: staff
        ? "rgba(229, 243, 255, 0.8)"
        : "rgba(250, 231, 229, 0.8)",
      linkStrong: staff
        ? "text-secondary hover:underline"
        : "text-primary hover:underline",
      linkFooter: staff
        ? "text-secondary font-decimal text-sm md:text-base font-semibold underline underline-offset-4 hover:text-secondary-dark"
        : "text-primary font-decimal text-sm md:text-base font-semibold underline underline-offset-4 hover:text-primary-dark",
    };
  }, [theme]);
}

function isLikelyHtml(s: string): boolean {
  return /<[a-z][\s\S]*>/i.test(s);
}

/** Cosmos may store list fields as `string[]` or one comma-separated `string`. */
function normalizeStringArray(raw: unknown): string[] {
  if (raw == null) {
    return [];
  }
  if (Array.isArray(raw)) {
    return raw.map((x) => String(x).trim()).filter(Boolean);
  }
  if (typeof raw === "string") {
    return raw
      .split(/[,;\n]/)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

/** APIs may return `string[]` or a single `string` for `industryOrganizationAffiliation`. */
function normalizeIndustryOrganizationAffiliation(raw: unknown): string[] {
  if (raw == null) {
    return [];
  }
  if (Array.isArray(raw)) {
    return raw.map((x) => String(x).trim()).filter(Boolean);
  }
  if (typeof raw === "string") {
    const t = raw.trim();
    return t ? [t] : [];
  }
  return [];
}

function ProfileViewMoreButton({
  onClick,
  className = "",
}: {
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group mb-2 inline-flex w-full items-center justify-end gap-2 font-decimal text-sm font-bold text-accent-Default-dark transition-opacity hover:opacity-90 md:text-base ${className}`}
    >
      <span>View More</span>
      <span
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-Default text-white"
        aria-hidden
      >
        <span className="flex h-2.5 w-2.5">
          <ChevronRight />
        </span>
      </span>
    </button>
  );
}

function isMainOffice(office: IOfficeNew): boolean {
  return String(office.officeType ?? "").toLowerCase() === "main";
}

function officeDisplayName(office: IOfficeNew & { name?: string }): string {
  const legacyName = String(office.name ?? "").trim();
  if (legacyName) {
    return legacyName;
  }

  return (
    getJurisdictionLabel(office) ||
    [office.stateName, office.cityName].filter(Boolean).join(", ") ||
    String(office.cityName ?? "").trim() ||
    String(office.id ?? "").trim()
  );
}

/** Read-only summary. Set `editFormAnchorId` when a detailed edit form exists below (scroll target). */
export interface ProfileEditModeOverviewProps {
  person: IPerson;
  offices: IOfficeNew[];
  /** When set, shows an “Update profile details” link that scrolls to this anchor id. */
  editFormAnchorId?: string;
  /** When true, the member can open the profile photo upload dialog (own profile only). */
  isOwnProfile?: boolean;
  /** Called after a new profile photo URL is saved to the server. */
  onProfilePhotoUpdated?: (photoTimestamp: string) => void;
  /** Called after social media URLs are saved (own profile only). */
  onSocialMediaSaved?: (socialNetworks: PersonSocialNetwork[]) => void;
  /** Called after areas of practice / expertise are saved (own attorney profile). */
  onAreasOfPracticeUpdated?: () => void;
  /** Called after General Profile (bio, languages, bar admissions) is saved. */
  onGeneralProfileUpdated?: () => void;
  /** Called after Professional Career fields are saved. */
  onProfessionalCareerUpdated?: () => void;
  /** Member public profile = red accents; staff-app profile = blue (`secondary`). */
  profileTheme?: ProfileOverviewBrand;
}

const ProfileEditModeOverview: React.FC<ProfileEditModeOverviewProps> = ({
  person,
  offices,
  editFormAnchorId,
  isOwnProfile = false,
  onProfilePhotoUpdated,
  onSocialMediaSaved,
  onAreasOfPracticeUpdated,
  onGeneralProfileUpdated,
  onProfessionalCareerUpdated,
  profileTheme = "member",
}) => {
  const brand = useProfileBrand(profileTheme);
  /** Inline pencils and modals stay member-only; staff editing is via the shell “Edit Profile” action. */
  const allowMemberInlineEdits = isOwnProfile && profileTheme !== "staff";
  const [profileImageModalOpen, setProfileImageModalOpen] = useState(false);
  const [socialMediaModalOpen, setSocialMediaModalOpen] = useState(false);
  const [aopModalOpen, setAopModalOpen] = useState(false);
  const [generalProfileModalOpen, setGeneralProfileModalOpen] = useState(false);
  const [professionalCareerModalOpen, setProfessionalCareerModalOpen] =
    useState(false);
  const [listMoreModal, setListMoreModal] = useState<
    null | "expertise" | "areas" | "industries" | "leadership"
  >(null);
  const { practiceIndustryData } = UsePracticeIndustryList();
  const { firmData } = UseFirmList();
  const [practiceIndustriesResolved, setPracticeIndustriesResolved] = useState<
    { id: string; name: string; icon?: string }[]
  >([]);
  const [leadership, setLeadership] = useState<ILeadershipEntry[]>([]);
  const [blogs, setBlogs] = useState<IBlogCard[]>([]);
  const [languageCatalog, setLanguageCatalog] = useState<ILanguage[]>([]);
  const [photoRenderKey, setPhotoRenderKey] = useState(0);

  useEffect(() => {
    const onProfilePhotoUpdated = (event: Event) => {
      const { personId } = (
        event as CustomEvent<{ personId: string; cacheBust: number }>
      ).detail;
      if (personId && personId === person.id) {
        setPhotoRenderKey((k) => k + 1);
      }
    };

    document.addEventListener(
      PROFILE_PHOTO_UPDATED_EVENT,
      onProfilePhotoUpdated,
    );
    return () => {
      document.removeEventListener(
        PROFILE_PHOTO_UPDATED_EVENT,
        onProfilePhotoUpdated,
      );
    };
  }, [person.id]);

  const profilePhotoSrc = useMemo(
    () =>
      person.id
        ? resolveProfilePictureDisplayUrl(person.id, person.photo, {
            width: 360,
            height: 360,
            photoUpdatedAt: person._ts,
          })
        : "",
    [person.id, person.photo, person._ts, photoRenderKey],
  );
  /** Person AOP rows loaded by `areasOfPractice` ids (id → displayName, practiceIndustryIds). */
  const [personAopCatalog, setPersonAopCatalog] = useState<IAreaOfPractice[]>(
    [],
  );
  const firmObj = useMemo(
    () => firmData.find((f: { id: string }) => f.id === person.firm),
    [firmData, person.firm],
  );

  /** Stricter than photo / general profile: needs firm (firm AOP catalog) + attorney or existing AOP rows. */
  const canEditAreasOfPractice = Boolean(
    allowMemberInlineEdits &&
    person.id?.trim() &&
    person.firm?.trim() &&
    personMayEditAreasOfPractice(person),
  );

  const canEditGeneralProfile = Boolean(
    allowMemberInlineEdits && person.id?.trim(),
  );

  const canEditProfessionalCareer = Boolean(
    allowMemberInlineEdits && person.id?.trim(),
  );

  const handleLanguageAddedToCatalog = useCallback((lang: ILanguage) => {
    setLanguageCatalog((prev) => {
      if (prev.some((l) => l.id === lang.id || l.name === lang.name)) {
        return prev;
      }
      return [...prev, lang].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
      );
    });
  }, []);

  const [firmLocalClockMs, setFirmLocalClockMs] = useState(() => Date.now());

  /** IANA zone from `firm.countryName` + `firm.cityName` via countries-and-timezones (no Region/Cosmos extra reads). */
  const firmIanaTimeZone = useMemo(
    () => resolveIanaFromFirmCountryCity(firmObj ?? undefined),
    [firmObj],
  );

  const firmLocalTimeLabel = useMemo(() => {
    if (!firmIanaTimeZone) {
      return "";
    }
    return formatFirmLocalTimeLine(firmIanaTimeZone, firmLocalClockMs);
  }, [firmIanaTimeZone, firmLocalClockMs]);

  useEffect(() => {
    if (!firmIanaTimeZone) {
      return undefined;
    }
    setFirmLocalClockMs(Date.now());
    const id = window.setInterval(
      () => setFirmLocalClockMs(Date.now()),
      60_000,
    );
    return () => window.clearInterval(id);
  }, [firmIanaTimeZone]);

  /** Resolved display names only (never store id as a “name”). */
  const aopNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of personAopCatalog) {
      if (a?.id) {
        const label = (a.displayName ?? "").trim();
        if (label) {
          m.set(a.id, label);
        }
      }
    }
    return m;
  }, [personAopCatalog]);

  const expertiseList = useMemo(() => {
    const ids = normalizePersonAopExpertiseIds(person);
    const out: { id: string; name: string }[] = [];
    for (const id of ids) {
      const name = aopNameById.get(id)?.trim();
      if (name) {
        out.push({ id, name });
      }
    }
    return out;
  }, [aopNameById, person.aopExpertiseIds]);

  const allAopsList = useMemo(() => {
    const ids = normalizePersonAreasOfPracticeIds(person);
    const out: { id: string; name: string }[] = [];
    for (const id of ids) {
      const name = aopNameById.get(id)?.trim();
      if (name) {
        out.push({ id, name });
      }
    }
    return out;
  }, [person.areasOfPractice, aopNameById]);

  const practiceIndustriesSorted = useMemo(() => {
    const rows = practiceIndustriesResolved.filter(
      (pi): pi is { id: string; name: string; icon?: string } =>
        pi != null && typeof pi.name === "string",
    );
    return [...rows].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
    );
  }, [practiceIndustriesResolved]);

  const isAttorney = isPersonAttorney(person);
  const displayExpertiseList = isAttorney ? expertiseList : [];
  const displayAllAopsList = isAttorney ? allAopsList : [];
  const displayPracticeIndustriesSorted = isAttorney
    ? practiceIndustriesSorted
    : [];

  /** Stable key so AOP / practice-industry resolution does not re-fetch on array identity churn. */
  const personAopIdsKey = useMemo(
    () => normalizePersonAreasOfPracticeIds(person).sort().join(","),
    [person.areasOfPractice],
  );

  /** Legacy fallback when `person.barAdmissions` is empty (label swap / older data). */
  const barAdmissionsPlainText = useMemo(() => {
    const raw = person.industryOrganizationAffiliation as unknown;
    if (raw == null) {
      return "";
    }
    if (typeof raw === "string") {
      return raw.trim();
    }
    if (Array.isArray(raw)) {
      return raw
        .map((x) => String(x).trim())
        .filter(Boolean)
        .join(", ");
    }
    return String(raw).trim();
  }, [person.industryOrganizationAffiliation]);

  /** Profile “Bar Admissions” block: prefer stored `barAdmissions`, else legacy industry field. */
  const profileBarAdmissionsForDisplay = useMemo(() => {
    const raw = person.barAdmissions as unknown;
    if (typeof raw === "string" && raw.trim()) {
      return raw.trim();
    }
    if (Array.isArray(raw) && raw.length) {
      return raw
        .map((x) => String(x).trim())
        .filter(Boolean)
        .join(", ");
    }
    return barAdmissionsPlainText;
  }, [person.barAdmissions, barAdmissionsPlainText]);

  const languageIds = useMemo(
    () => normalizeStringArray(person.languages as unknown),
    [person.languages],
  );

  const languageById = useMemo(() => {
    const m = new Map<string, ILanguage>();
    languageCatalog.forEach((row) => {
      if (row.id) {
        m.set(row.id, row);
      }
    });
    return m;
  }, [languageCatalog]);

  const resolvedCatalogLanguages = useMemo(() => {
    return languageIds.map((id) => {
      const row = languageById.get(id);
      if (row) {
        return { id, name: row.name };
      }
      return { id, name: id };
    });
  }, [languageIds, languageById]);

  /** Single line under firm name: main office only. */
  const mainOfficeDisplayName = useMemo(() => {
    const list = Array.isArray(offices) ? offices : [];
    const mainId = person.mainOffice?.trim() || "";
    if (person.mainOfficeName?.trim()) {
      return person.mainOfficeName.trim();
    }
    if (mainId) {
      const main = list.find((x) => x.id === mainId);
      if (main) {
        return officeDisplayName(main);
      }
    }
    const fallback = list.find(isMainOffice) ?? list[0];
    return fallback ? officeDisplayName(fallback) : "";
  }, [person.mainOfficeName, person.mainOffice, offices]);

  useEffect(() => {
    let cancelled = false;
    getLanguages()
      .then((rows) => {
        if (!cancelled) {
          setLanguageCatalog(rows ?? []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLanguageCatalog([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const personAopIds = normalizePersonAreasOfPracticeIds(person);
        const [personAops, leadershipEntries] = await Promise.all([
          getAOPsByIds(personAopIds),
          person.id ? getPersonLeadership(person.id) : Promise.resolve([]),
        ]);
        if (cancelled) {
          return;
        }
        setLeadership(leadershipEntries ?? []);
        const list = Array.isArray(personAops) ? personAops : [];
        setPersonAopCatalog(list);

        const piIds = new Set<string>();
        list.forEach((aop: { practiceIndustryIds?: string[] }) => {
          aop.practiceIndustryIds?.forEach((id: string) => piIds.add(id));
        });
        const catalog = Array.isArray(practiceIndustryData)
          ? practiceIndustryData
          : [];
        const resolved = Array.from(piIds)
          .map((id) => catalog.find((pi: { id: string }) => pi.id === id))
          .filter(
            (pi): pi is { id: string; name: string; icon?: string } =>
              pi != null && typeof (pi as { name?: string }).name === "string",
          );
        setPracticeIndustriesResolved(resolved);
      } catch {
        if (!cancelled) {
          setPracticeIndustriesResolved([]);
          setLeadership([]);
          setPersonAopCatalog([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [person.id, personAopIdsKey, practiceIndustryData]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!person.id || !person.firm) {
        setBlogs([]);
        return;
      }
      try {
        const blogRows = await getBlogsByAuthorName({
          firstName: person.name,
          lastName: person.lastName,
          limit: 8,
        });
        if (cancelled) {
          return;
        }
        setBlogs(blogRows);
      } catch {
        if (!cancelled) {
          setBlogs([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [person.id, person.firm, person.name, person.lastName]);

  /** Work / direct person fields first; many profiles only have phone on the main office. */
  const profileContactPhone = useMemo(() => {
    /** API / form sometimes send `phoneNumber` as `{ code, phone, basecode }` or a number; avoid `.trim` on non-strings. */
    const tryParts = (
      cc: unknown,
      num: unknown,
    ): { cc: string; num: string } | null => {
      const trimStr = (v: unknown): string => {
        if (v == null) {
          return "";
        }
        if (typeof v === "string") {
          return v.trim();
        }
        if (typeof v === "number" || typeof v === "boolean") {
          return String(v).trim();
        }
        return "";
      };
      let a = trimStr(cc);
      let b = trimStr(num);
      if (
        typeof num === "object" &&
        num !== null &&
        ("phone" in num || "code" in num)
      ) {
        const o = num as { code?: string; phone?: string };
        if (!a) {
          a = trimStr(o.code);
        }
        if (!b) {
          b = trimStr(o.phone);
        }
      }
      if (!a && !b) {
        return null;
      }
      return { cc: a, num: b };
    };

    let parts = tryParts(person.countryCodePhoneNumber, person.phoneNumber);
    if (!parts) {
      parts = tryParts(
        person.directCountryCodePhoneNumber,
        person.directPhoneNumber,
      );
    }
    if (!parts) {
      const list = Array.isArray(offices) ? offices : [];
      const oid = person.mainOffice?.trim();
      const main = oid
        ? list.find((x) => x.id === oid)
        : (list.find(isMainOffice) ?? list[0]);
      const op = main?.phoneNumber;
      if (op) {
        parts = tryParts(op.code, op.phone);
      }
    }

    if (!parts) {
      return { display: "" };
    }
    const display = [parts.cc, parts.num].filter(Boolean).join(" ");
    return { display };
  }, [
    person.countryCodePhoneNumber,
    person.phoneNumber,
    person.directCountryCodePhoneNumber,
    person.directPhoneNumber,
    person.mainOffice,
    offices,
  ]);

  const bioHtml = person.bio?.trim() ?? "";
  const educationText = person.education?.trim() ?? "";
  const publicationsText = person.publications?.trim() ?? "";

  /**
   * Affiliations from `industryOrganizationAffiliation`. One HTML blob → rich render; else list lines.
   */
  const affiliationsDisplayHtml = useMemo(() => {
    const parts = normalizeIndustryOrganizationAffiliation(
      person.industryOrganizationAffiliation as unknown,
    );
    if (parts.length === 0) {
      return null;
    }
    if (parts.length === 1 && isLikelyHtml(parts[0])) {
      return parts[0];
    }
    return null;
  }, [person.industryOrganizationAffiliation]);

  const affiliationLines = useMemo(() => {
    if (affiliationsDisplayHtml) {
      return [];
    }
    const parts = normalizeIndustryOrganizationAffiliation(
      person.industryOrganizationAffiliation as unknown,
    );
    const fromAdditional = (person.additionalAffiliations ?? [])
      .map((x) => String(x).trim())
      .filter(Boolean);
    const fromParts = parts.flatMap((entry) => {
      const t = String(entry).trim();
      if (!t) {
        return [];
      }
      if (AFFILIATION_ENUM_VALUE_SET.has(t)) {
        return [t];
      }
      return t
        .split(/[,;\n]/)
        .map((s) => s.trim())
        .filter(Boolean);
    });
    return [...new Set([...fromParts, ...fromAdditional])].filter(Boolean);
  }, [
    person.industryOrganizationAffiliation,
    person.additionalAffiliations,
    affiliationsDisplayHtml,
  ]);

  /** Shown under HTML affiliations block when free-text slots exist. */
  const standaloneAdditionalAffiliations = useMemo(
    () =>
      (person.additionalAffiliations ?? [])
        .map((x) => String(x).trim())
        .filter(Boolean),
    [person.additionalAffiliations],
  );

  const additionalActivitiesBody = useMemo(() => {
    const legacy =
      person.professionalActivitiesAndPreviousExperience?.trim() ?? "";
    const canonical = person.additionalActivities?.trim() ?? "";
    const pastEmployment = person.pastEmploymentPositions?.trim() ?? "";
    return canonical || legacy || pastEmployment;
  }, [
    person.professionalActivitiesAndPreviousExperience,
    person.additionalActivities,
    person.pastEmploymentPositions,
  ]);

  const hasProfessionalCareerContent =
    Boolean(person.significantAccomplishments?.trim()) ||
    Boolean(affiliationsDisplayHtml) ||
    affiliationLines.length > 0 ||
    standaloneAdditionalAffiliations.length > 0 ||
    Boolean(additionalActivitiesBody) ||
    Boolean(person.seminarPresentation?.trim()) ||
    Boolean(person.additionalInformation?.trim());

  const hasProfileOverviewContent =
    Boolean(bioHtml) ||
    languageIds.length > 0 ||
    Boolean(profileBarAdmissionsForDisplay) ||
    Boolean(educationText);

  const showProfileOverviewSection =
    hasProfileOverviewContent || canEditGeneralProfile;

  return (
    <div className="w-full pb-8">
      {/* Main card — neutral-light #FFF; ≥1200px: 3×2 grid so row1 = person | practice expertise | WSG industries, row2 = firm | areas | WSG leadership */}
      <div
        className={`mb-8 flex w-full flex-col justify-center self-stretch gap-5 rounded-none border border-gray-100 bg-white p-6 shadow-sm md:gap-6 min-[1200px]:grid min-[1200px]:grid-cols-[minmax(0,4fr)_minmax(0,3fr)_minmax(0,3fr)] min-[1200px]:grid-rows-[auto_auto] min-[1200px]:items-start min-[1200px]:gap-x-8 min-[1200px]:gap-y-5 min-[1200px]:p-10${
          allowMemberInlineEdits ? " min-[1200px]:pt-16" : ""
        }`}
      >
        {/* Column 1 — &lt;md: profile over firm; md–1199px: profile | firm; ≥1200px: display contents → children join parent grid */}
        <div className="flex min-w-0 flex-1 flex-col gap-6 self-stretch border-b border-gray-100 pb-6 min-[1200px]:contents min-[1200px]:border-b-0 min-[1200px]:pb-0">
          <div className="flex w-full max-md:items-center flex-col gap-6 md:flex-row md:items-stretch md:gap-8 min-[1200px]:contents">
            {/* Profile block — socials in text column (same as firm) */}
            <div className="flex min-w-0 w-full max-md:items-center flex-1 flex-col gap-4 md:items-stretch min-[1200px]:col-start-1 min-[1200px]:row-start-1 min-[1200px]:border-r min-[1200px]:border-gray-100 min-[1200px]:pr-12 min-[1200px]:flex-none">
              <div className="flex w-full flex-col items-center gap-6 self-stretch p-0 text-center max-md:flex-col min-[1200px]:flex-row min-[1200px]:items-start min-[1200px]:text-left">
                <div className="relative z-20 shrink-0 max-md:mx-auto">
                  {allowMemberInlineEdits && person.id ? (
                    <>
                      <button
                        type="button"
                        onClick={() => setProfileImageModalOpen(true)}
                        className={`group relative h-[9rem] w-[9rem] shrink-0 rounded-full border-2 ${brand.borderLight} bg-gray-50 text-left shadow-sm ring-offset-2 transition hover:ring-2 focus:outline-none focus-visible:ring-2 ${brand.ringGroup}`}
                        title="Change profile photo"
                        aria-label="Change profile photo"
                      >
                        <div className="h-full w-full overflow-hidden rounded-full">
                          <ProfilePhoto
                            src={profilePhotoSrc}
                            imgKey={`${person.id}-${photoRenderKey}`}
                            imgClassName="h-full w-full object-cover transition group-hover:brightness-[0.97]"
                          />
                        </div>
                        <span
                          className="group absolute -right-0.5 -top-0.5 z-30 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full shadow-sm transition hover:scale-105 hover:shadow-md hover:brightness-[0.96]"
                          style={{ background: brand.pencilOverlayBg }}
                          aria-hidden
                        >
                          <span
                            className={`flex h-5 w-5 shrink-0 items-center justify-center pt-[2.5px] pr-[1.667px] pb-[1.667px] pl-[2.5px] transition-colors ${brand.text} ${brand.textHover}`}
                          >
                            <RedPencil className="h-full w-full" />
                          </span>
                        </span>
                      </button>
                      <UploadProfileImageModal
                        isOpen={profileImageModalOpen}
                        onClose={() => setProfileImageModalOpen(false)}
                        person={person}
                        initialPhotoUrl={profilePhotoSrc || null}
                        onPhotoSaved={(timestamp) => onProfilePhotoUpdated?.(timestamp)}
                      />
                    </>
                  ) : (
                    <div
                      className={`h-[9rem] w-[9rem] overflow-hidden rounded-full border-2 ${brand.borderLight} bg-gray-50`}
                    >
                      <ProfilePhoto
                        src={profilePhotoSrc}
                        imgKey={`${person.id}-${photoRenderKey}`}
                      />
                    </div>
                  )}
                </div>
                <div className="min-w-0 w-full max-w-full flex-1 max-md:text-center min-[1200px]:text-left">
                  <h3
                    className={`2xl:text-[28px] font-semibold leading-tight text-[26px] ${brand.text}`}
                  >
                    {displayPersonName(person)}
                  </h3>
                  {person.unlistedTitle ? (
                    <p className="mt-1 text-[20px] md:text-[21px] text-gray-font">
                      {person.unlistedTitle}
                    </p>
                  ) : (
                    <p className="mt-1 text-[20px] md:text-[21px] text-gray-font">
                      {person.title}
                    </p>
                  )}
                  <div className="mt-3 flex min-w-0 max-w-full justify-center min-[1200px]:justify-start">
                    <SocialLinks
                      user={person}
                      isEditable={false}
                      profileTheme={profileTheme}
                      rowClassName="justify-center min-[1200px]:justify-start"
                      suffix={
                        allowMemberInlineEdits && person.id ? (
                          <button
                            type="button"
                            onClick={() => setSocialMediaModalOpen(true)}
                            className={`flex h-[1.25rem] w-[1.25rem] shrink-0 items-center justify-center transition-colors ${brand.text} ${brand.textHover}`}
                            aria-label="Edit social media"
                          >
                            <RedPencil className="h-full w-full" />
                          </button>
                        ) : null
                      }
                    />
                  </div>
                  {allowMemberInlineEdits && person.id ? (
                    <EditSocialMediaModal
                      isOpen={socialMediaModalOpen}
                      onClose={() => setSocialMediaModalOpen(false)}
                      person={person}
                      onSaved={(networks) => onSocialMediaSaved?.(networks)}
                    />
                  ) : null}
                  {person.firmEmail?.trim() ? (
                    <a
                      href={`mailto:${person.firmEmail.trim()}`}
                      className="mt-4 flex items-center justify-center gap-2 text-base lg:text-[17px] 2xl:text-[18px] text-gray-font hover:underline min-[1200px]:justify-start"
                    >
                      <Email className="h-5 w-5 shrink-0 text-gray-font" />
                      Send an email
                    </a>
                  ) : null}
                  {profileContactPhone.display ? (
                    <div className="mt-2 flex items-center justify-center gap-2 text-base lg:text-[17px] 2xl:text-[18px] text-gray-font min-[1200px]:justify-start">
                      <Phone
                        className="h-5 w-5 shrink-0 text-gray-font [&_path]:fill-current"
                        aria-hidden
                      />
                      <span>{profileContactPhone.display}</span>
                    </div>
                  ) : null}
                  {person.yearJoinedFirm ? (
                    <p className="mt-2 text-sm text-gray-500 text-center min-[1200px]:text-left">
                      Joined {person.yearJoinedFirm}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Firm block — below profile on mobile/lg; right of profile on md–lg */}
            <div className="flex min-w-0 w-full max-md:items-center flex-1 flex-col gap-4 md:items-stretch min-[1200px]:col-start-1 min-[1200px]:row-start-2 min-[1200px]:border-r min-[1200px]:border-gray-100 min-[1200px]:pr-12 min-[1200px]:flex-none">
              <div className="flex w-full flex-col items-center gap-6 self-stretch p-0 text-center max-md:flex-col min-[1200px]:flex-row min-[1200px]:items-start min-[1200px]:text-left">
                <div
                  className={`flex h-[9rem] w-[9rem] shrink-0 max-md:mx-auto items-center justify-center overflow-hidden rounded-full border-2 ${brand.borderLight} bg-gray-50 shadow-sm`}
                >
                  {person.firmLogo || firmObj?.logo ? (
                    <img
                      src={person.firmLogo || firmObj?.logo}
                      alt=""
                      className="h-full w-full object-contain"
                    />
                  ) : (
                    <span className="px-1 text-center text-xs text-gray-400">
                      Firm
                    </span>
                  )}
                </div>
                <div className="min-w-0 w-full max-w-full flex-1 max-md:text-center min-[1200px]:text-left">
                  <h3 className="text-[20px] sm:text-[22px] font-bold text-accent-Default-dark">
                    {person.firmName || firmObj?.firmName || "—"}
                  </h3>
                  <p className="mt-3 text-xl text-gray-font max-md:text-center min-[1200px]:text-left">
                    {mainOfficeDisplayName || "—"}
                  </p>
                  {firmObj || firmLocalTimeLabel ? (
                    <div className="mt-3 flex flex-col items-center gap-3 max-md:items-center min-[1200px]:items-start">
                      {firmObj ? (
                        <div className="flex w-full flex-wrap items-center justify-center gap-2 min-[1200px]:justify-start">
                          <SocialLinks
                            organization
                            organizationObj={firmObj}
                            user={person}
                            isEditable={false}
                            profileTheme={profileTheme}
                            rowClassName="justify-center min-[1200px]:justify-start"
                          />
                        </div>
                      ) : null}
                      {firmLocalTimeLabel ? (
                        <p className="w-full text-base sm:text-[17px] 2xl:text-[18px] text-gray-400 max-md:text-center min-[1200px]:text-left">
                          {firmLocalTimeLabel}
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tablet: practice blocks | WSG blocks side by side; at 1200px+ wrapper uses display:contents so three columns match desktop. */}
        <div className="flex w-full min-w-0 flex-col gap-3 min-[1200px]:contents">
          {allowMemberInlineEdits ? (
            <a
              href={MY_PREFERENCES_URL}
              className="inline-flex w-full max-w-full items-center justify-center gap-2 font-decimal text-base font-bold uppercase tracking-wide text-accent-Default-dark no-underline transition-opacity hover:opacity-90 md:justify-start min-[1200px]:hidden"
            >
              <span>Profile Preferences</span>
              <span className="flex h-8 w-8 shrink-0 text-accent-Default-dark [&_svg]:h-full [&_svg]:w-full [&_svg]:fill-current">
                <ProfilePreferences />
              </span>
            </a>
          ) : null}
          <div className="flex w-full min-w-0 flex-col gap-6 md:flex-row md:items-stretch md:gap-6 min-[1200px]:contents">
            <div className="flex min-w-0 max-md:items-center flex-1 flex-col gap-4 self-stretch justify-between text-center md:min-w-0 md:flex-1 md:items-stretch md:text-left min-[1200px]:contents min-[1200px]:gap-0">
              <div className="relative flex min-h-0 flex-1 flex-col items-center gap-2 self-stretch md:items-start min-[1200px]:col-start-2 min-[1200px]:row-start-1 min-[1200px]:flex-none min-[1200px]:self-start min-[1200px]:pl-5">
                {allowMemberInlineEdits ? (
                  <a
                    href={MY_PREFERENCES_URL}
                    className="absolute bottom-full left-5 mb-2 hidden w-auto items-center justify-start gap-2 font-decimal text-base font-bold uppercase tracking-wide text-accent-Default-dark no-underline transition-opacity hover:opacity-90 min-[1200px]:inline-flex"
                  >
                    <span>Profile Preferences</span>
                    <span className="flex h-8 w-8 shrink-0 text-accent-Default-dark [&_svg]:h-full [&_svg]:w-full [&_svg]:fill-current">
                      <ProfilePreferences />
                    </span>
                  </a>
                ) : null}
                <div className="flex w-full items-center justify-center gap-2 md:justify-start">
                  <h3
                    className={`min-w-0 font-decimal text-lg font-semibold tracking-wide ${brand.text}`}
                  >
                    Practice Expertise
                  </h3>
                  {canEditAreasOfPractice ? (
                    <button
                      type="button"
                      onClick={() => setAopModalOpen(true)}
                      className={`shrink-0 transition-colors ${brand.text} ${brand.textHover}`}
                      aria-label="Edit areas of practice and expertise"
                    >
                      <RedPencil className={`h-5 w-5 ${brand.text}`} />
                    </button>
                  ) : null}
                </div>
                {displayExpertiseList.length > 0 ? (
                  <>
                    <ul className="w-full list-inside list-disc space-y-1.5 text-base sm:text-[17px] 2xl:text-[18px] text-gray-font marker:text-gray-font max-md:mx-auto max-md:inline-block max-md:w-max max-md:text-left md:block md:text-base">
                      {displayExpertiseList
                        .slice(0, PRACTICE_EXPERTISE_PREVIEW_MAX)
                        .map((aop) => (
                          <li key={aop.id} className="pl-0.5">
                            {aop.name}
                          </li>
                        ))}
                    </ul>
                    {displayExpertiseList.length > PRACTICE_EXPERTISE_PREVIEW_MAX ? (
                      <ProfileViewMoreButton
                        className="mt-1"
                        onClick={() => setListMoreModal("expertise")}
                      />
                    ) : null}
                  </>
                ) : (
                  <p className="w-full max-md:text-center text-sm text-gray-400 md:text-base">
                    —
                  </p>
                )}
              </div>
              <div className="flex min-h-0 flex-1 flex-col items-center gap-2 self-stretch md:items-start min-[1200px]:col-start-2 min-[1200px]:row-start-2 min-[1200px]:flex-none min-[1200px]:self-start min-[1200px]:pl-5">
                <div className="flex w-full items-center justify-center gap-2 md:justify-start">
                  <h3
                    className={`min-w-0 font-decimal text-lg font-semibold tracking-wide ${brand.text}`}
                  >
                    Areas of Practice
                  </h3>
                  {canEditAreasOfPractice ? (
                    <button
                      type="button"
                      onClick={() => setAopModalOpen(true)}
                      className={`shrink-0 transition-colors ${brand.text} ${brand.textHover}`}
                      aria-label="Edit areas of practice and expertise"
                    >
                      <RedPencil className={`h-5 w-5 ${brand.text}`} />
                    </button>
                  ) : null}
                </div>
                {displayAllAopsList.length > 0 ? (
                  <>
                    <ul className="w-full list-inside list-disc space-y-1.5 text-base sm:text-[17px] 2xl:text-[18px] text-gray-font marker:text-gray-font max-md:mx-auto max-md:inline-block max-md:w-max max-md:text-left md:block md:text-base">
                      {displayAllAopsList
                        .slice(0, AREAS_OF_PRACTICE_PREVIEW_MAX)
                        .map((a) => (
                          <li key={a.id} className="pl-0.5">
                            {a.name}
                          </li>
                        ))}
                    </ul>
                    {displayAllAopsList.length > AREAS_OF_PRACTICE_PREVIEW_MAX ? (
                      <ProfileViewMoreButton
                        className="mt-1"
                        onClick={() => setListMoreModal("areas")}
                      />
                    ) : null}
                  </>
                ) : (
                  <p className="w-full max-md:text-center text-sm text-gray-400 md:text-base">
                    —
                  </p>
                )}
              </div>
            </div>

            <div className="flex min-w-0 max-md:items-center flex-1 flex-col gap-4 self-stretch justify-between text-center md:min-w-0 md:flex-1 md:items-stretch md:text-left min-[1200px]:contents min-[1200px]:gap-0">
              <div className="flex min-h-0 flex-1 flex-col items-center gap-2 self-stretch md:items-start min-[1200px]:col-start-3 min-[1200px]:row-start-1 min-[1200px]:flex-none min-[1200px]:self-start min-[1200px]:pl-5">
                <div className="flex w-full items-center justify-center gap-2 md:justify-start">
                  <h3
                    className={`min-w-0 font-decimal text-lg font-semibold tracking-wide ${brand.text}`}
                  >
                    WSG Practice Industries
                  </h3>
                  {canEditAreasOfPractice ? (
                    <button
                      type="button"
                      onClick={() => setAopModalOpen(true)}
                      className={`shrink-0 transition-colors ${brand.text} ${brand.textHover}`}
                      aria-label="Edit areas of practice and expertise"
                    >
                      <RedPencil className={`h-5 w-5 ${brand.text}`} />
                    </button>
                  ) : null}
                </div>
                {displayPracticeIndustriesSorted.length > 0 ? (
                  <>
                    <div className="flex w-full min-w-0 flex-col gap-y-1.5">
                      {displayPracticeIndustriesSorted
                        .slice(0, WSG_PRACTICE_INDUSTRIES_PREVIEW_MAX)
                        .map((pi) => (
                          <div
                            key={pi.id}
                            className="flex min-w-0 items-center gap-2 max-md:justify-center md:justify-start"
                          >
                            <div className="h-5 w-5 shrink-0">
                              <SvgIcon practiceId={pi.id} iconUrl={pi.icon} />
                            </div>
                            <span className="text-base sm:text-[17px] 2xl:text-[18px] text-gray-font md:text-base">
                              {pi.name}
                            </span>
                          </div>
                        ))}
                    </div>
                    {displayPracticeIndustriesSorted.length >
                    WSG_PRACTICE_INDUSTRIES_PREVIEW_MAX ? (
                      <ProfileViewMoreButton
                        className="mt-1"
                        onClick={() => setListMoreModal("industries")}
                      />
                    ) : null}
                  </>
                ) : (
                  <p className="w-full max-md:text-center text-sm text-gray-400 md:text-base">
                    —
                  </p>
                )}
              </div>
              <div className="flex min-h-0 flex-1 flex-col items-start gap-2 self-stretch min-[1200px]:col-start-3 min-[1200px]:row-start-2 min-[1200px]:flex-none min-[1200px]:self-start min-[1200px]:pl-5">
                <h3
                  className={`mb-1 w-full font-decimal text-lg font-semibold tracking-wide ${brand.text}`}
                >
                  WSG Leadership
                </h3>
                {leadership.length > 0 ? (
                  <>
                    <ul className={WSG_LEADERSHIP_LIST_CLASS}>
                      {leadership
                        .slice(0, WSG_LEADERSHIP_PREVIEW_MAX)
                        .map((entry) => (
                          <li key={entry.key} className={WSG_LEADERSHIP_ITEM_CLASS}>
                            {entry.label}
                          </li>
                        ))}
                    </ul>
                    {leadership.length > WSG_LEADERSHIP_PREVIEW_MAX ? (
                      <ProfileViewMoreButton
                        className="mt-1"
                        onClick={() => setListMoreModal("leadership")}
                      />
                    ) : null}
                  </>
                ) : (
                  <p className="w-full max-md:text-center text-sm text-gray-400 md:text-base">
                    —
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showProfileOverviewSection ? (
        <ProfileOverviewSection
          variant={brand.sectionVariant}
          title="Profile"
          defaultOpen
          headerActions={
            canEditGeneralProfile ? (
              <button
                type="button"
                onClick={() => setGeneralProfileModalOpen(true)}
                className="inline-flex items-center gap-2 whitespace-nowrap font-decimal text-sm font-normal text-white hover:opacity-95 md:text-base"
                aria-label="Edit profile"
              >
                <span>Edit Profile</span>
                <RedPencil className="h-5 w-5 shrink-0 text-white" />
              </button>
            ) : null
          }
        >
          {hasProfileOverviewContent ? (
            <div className="divide-y divide-gray-200">
              {bioHtml ? (
                <div className="pb-6">
                  <div className="prose prose-gray max-w-none">
                    {isLikelyHtml(bioHtml) ? (
                      <div
                        className="text-gray-font text-base sm:text-[17px] 2xl:text-[18px] leading-relaxed [&_p]:mb-3 [&_p:last-child]:mb-0"
                        dangerouslySetInnerHTML={{ __html: bioHtml }}
                      />
                    ) : (
                      <p className="text-gray-font text-base sm:text-[17px] 2xl:text-[18px] leading-relaxed whitespace-pre-wrap">
                        {bioHtml}
                      </p>
                    )}
                  </div>
                </div>
              ) : null}

              {languageIds.length > 0 ? (
                <div className="py-6">
                  <h3 className={PROFILE_CARD_SUBTITLE_ROBOTO}>Languages</h3>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
                    {resolvedCatalogLanguages.map((lang) => (
                      <span
                        key={lang.id}
                        className="min-w-0 text-base sm:text-[17px] 2xl:text-[18px] text-gray-font"
                      >
                        {lang.name}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}

              {profileBarAdmissionsForDisplay ? (
                <div className="py-6">
                  <h3 className="mb-4 text-sm sm:text-[17px] 2xl:text-[18px] font-bold text-gray-font">
                    Bar Admissions
                  </h3>
                  <div className="prose prose-gray max-w-none text-base sm:text-[17px] 2xl:text-[18px] leading-relaxed text-gray-font">
                    {isLikelyHtml(profileBarAdmissionsForDisplay) ? (
                      <div
                        className="[&_p]:mb-3 [&_p:last-child]:mb-0"
                        dangerouslySetInnerHTML={{
                          __html: profileBarAdmissionsForDisplay,
                        }}
                      />
                    ) : (
                      <p className="whitespace-pre-wrap">
                        {profileBarAdmissionsForDisplay}
                      </p>
                    )}
                  </div>
                </div>
              ) : null}

              {educationText ? (
                <div className="pt-6">
                  <h3 className={PROFILE_CARD_SUBTITLE_ROBOTO}>Education</h3>
                  <div className="prose prose-gray max-w-none text-base sm:text-[17px] 2xl:text-[18px] leading-relaxed text-gray-font">
                    {isLikelyHtml(educationText) ? (
                      <div
                        className="[&_p]:mb-3 [&_p:last-child]:mb-0"
                        dangerouslySetInnerHTML={{ __html: educationText }}
                      />
                    ) : (
                      <p className="whitespace-pre-wrap">{educationText}</p>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          ) : canEditGeneralProfile ? (
            <p className="text-sm text-gray-400 md:text-base">
              No profile summary yet. Use Edit Profile to add your bio,
              languages, bar admissions, and education.
            </p>
          ) : null}
        </ProfileOverviewSection>
      ) : null}

      {displayAllAopsList.length > 0 || canEditAreasOfPractice ? (
        <ProfileOverviewSection
          variant={brand.sectionVariant}
          title="Areas of Practice"
          defaultOpen
          headerActions={
            canEditAreasOfPractice ? (
              <button
                type="button"
                onClick={() => setAopModalOpen(true)}
                className="inline-flex max-w-full items-center gap-2 whitespace-nowrap font-decimal text-sm font-normal text-white hover:opacity-95 md:text-base"
                aria-label="Edit areas of practice"
              >
                <span>Edit AOP</span>
                <RedPencil className="h-5 w-5 shrink-0 text-white" />
              </button>
            ) : null
          }
        >
          {displayAllAopsList.length > 0 ? (
            <div>
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-base sm:text-[17px] 2xl:text-[18px] text-gray-font">
                {displayAllAopsList.map((a) => (
                  <span
                    key={a.id}
                    className="inline-flex min-w-0 max-w-full items-center gap-2"
                  >
                    <span
                      className="shrink-0 text-base sm:text-[17px] 2xl:text-[18px] text-gray-font"
                      aria-hidden
                    >
                      •
                    </span>
                    <span className="min-w-0 text-base sm:text-[17px] 2xl:text-[18px] text-gray-font">
                      {a.name}
                    </span>
                  </span>
                ))}
              </div>
              {displayExpertiseList.length > 0 ? (
                <>
                  <hr className="my-6 border-0 border-t border-gray-200" />
                  <div>
                    <h3 className={PROFILE_CARD_SUBTITLE_ROBOTO}>
                      Practice Expertise
                    </h3>
                    <div className="flex flex-wrap gap-x-6 gap-y-2 text-base text-gray-font">
                      {displayExpertiseList.map((a) => (
                        <span
                          key={a.id}
                          className="inline-flex min-w-0 max-w-full items-center gap-2"
                        >
                          <span
                            className="shrink-0 text-base sm:text-[17px] 2xl:text-[18px] text-gray-font"
                            aria-hidden
                          >
                            •
                          </span>
                          <span className="min-w-0 text-base sm:text-[17px] 2xl:text-[18px] text-gray-font">
                            {a.name}
                          </span>
                        </span>
                      ))}
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          ) : canEditAreasOfPractice ? (
            <p className="text-sm text-gray-400 md:text-base">
              No areas of practice yet. Use Edit AOP to add your practice areas,
              expertise, and industries.
            </p>
          ) : null}
        </ProfileOverviewSection>
      ) : null}

      {hasProfessionalCareerContent || canEditProfessionalCareer ? (
        <ProfileOverviewSection
          variant={brand.sectionVariant}
          title="Professional career"
          defaultOpen
          headerActions={
            canEditProfessionalCareer ? (
              <button
                type="button"
                onClick={() => setProfessionalCareerModalOpen(true)}
                className="inline-flex max-w-full items-center gap-2 whitespace-nowrap font-decimal text-sm font-normal text-white hover:opacity-95 md:text-base"
                aria-label="Edit professional career"
              >
                <span>Edit Professional Career</span>
                <RedPencil className="h-5 w-5 shrink-0 text-white" />
              </button>
            ) : null
          }
        >
          {hasProfessionalCareerContent ? (
            <div className="divide-y divide-gray-200">
              {person.significantAccomplishments?.trim() ? (
                <div className="py-6 [&:first-child]:pt-0">
                  <h3 className={PROFILE_CARD_SUBTITLE_ROBOTO}>
                    Significant Accomplishments
                  </h3>
                  {isLikelyHtml(person.significantAccomplishments) ? (
                    <div
                      className="prose prose-gray max-w-none text-base sm:text-[17px] 2xl:text-[18px] leading-relaxed text-gray-font [&_p]:mb-3 [&_p:last-child]:mb-0"
                      dangerouslySetInnerHTML={{
                        __html: person.significantAccomplishments,
                      }}
                    />
                  ) : (
                    <p className="whitespace-pre-wrap text-base sm:text-[17px] 2xl:text-[18px] text-gray-font">
                      {person.significantAccomplishments}
                    </p>
                  )}
                </div>
              ) : null}
              {affiliationsDisplayHtml ? (
                <div className="py-6 [&:first-child]:pt-0">
                  <h3 className={PROFILE_CARD_SUBTITLE_ROBOTO}>Affiliations</h3>
                  <div
                    className="prose prose-gray max-w-none text-base sm:text-[17px] 2xl:text-[18px] leading-relaxed text-gray-font [&_p]:mb-3 [&_p:last-child]:mb-0"
                    dangerouslySetInnerHTML={{
                      __html: affiliationsDisplayHtml,
                    }}
                  />
                  {standaloneAdditionalAffiliations.length > 0 ? (
                    <ul className="mt-4 grid list-none grid-cols-1 gap-x-6 gap-y-2 text-base sm:text-[17px] 2xl:text-[18px] text-gray-font sm:grid-cols-3 lg:grid-cols-4">
                      {standaloneAdditionalAffiliations.map((line, i) => (
                        <li
                          key={`add-aff-${line}-${i}`}
                          className="flex min-w-0 items-start gap-2"
                        >
                          <span
                            className="mt-0.5 shrink-0 text-gray-font"
                            aria-hidden
                          >
                            •
                          </span>
                          <span className="min-w-0 text-base sm:text-[17px] 2xl:text-[18px] text-gray-font">
                            {line}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ) : affiliationLines.length > 0 ? (
                <div className="py-6 [&:first-child]:pt-0">
                  <h3 className={PROFILE_CARD_SUBTITLE_ROBOTO}>Affiliations</h3>
                  <ul className="grid list-none grid-cols-1 gap-x-6 gap-y-2 text-base sm:text-[17px] 2xl:text-[18px] text-gray-font sm:grid-cols-3 lg:grid-cols-4">
                    {affiliationLines.map((line, i) => (
                      <li
                        key={`${line}-${i}`}
                        className="flex min-w-0 items-start gap-2"
                      >
                        <span
                          className="mt-0.5 shrink-0 text-gray-font"
                          aria-hidden
                        >
                          •
                        </span>
                        <span className="min-w-0 text-base sm:text-[17px] 2xl:text-[18px] text-gray-font">
                          {line}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {additionalActivitiesBody ? (
                <div className="py-6 [&:first-child]:pt-0">
                  <h3 className={PROFILE_CARD_SUBTITLE_ROBOTO}>
                    Additional Activities
                  </h3>
                  {isLikelyHtml(additionalActivitiesBody) ? (
                    <div
                      className="prose prose-gray max-w-none text-base sm:text-[17px] 2xl:text-[18px] leading-relaxed text-gray-font [&_p]:mb-3 [&_p:last-child]:mb-0"
                      dangerouslySetInnerHTML={{
                        __html: additionalActivitiesBody,
                      }}
                    />
                  ) : (
                    <p className="whitespace-pre-wrap text-base sm:text-[17px] 2xl:text-[18px] text-gray-font">
                      {additionalActivitiesBody}
                    </p>
                  )}
                </div>
              ) : null}
              {person.seminarPresentation?.trim() ? (
                <div className="py-6 [&:first-child]:pt-0">
                  <h3 className={PROFILE_CARD_SUBTITLE_ROBOTO}>
                    Seminar Presentation
                  </h3>
                  {isLikelyHtml(person.seminarPresentation) ? (
                    <div
                      className="prose prose-gray max-w-none text-base sm:text-[17px] 2xl:text-[18px] leading-relaxed text-gray-font [&_p]:mb-3 [&_p:last-child]:mb-0"
                      dangerouslySetInnerHTML={{
                        __html: person.seminarPresentation,
                      }}
                    />
                  ) : (
                    <p className="whitespace-pre-wrap text-base sm:text-[17px] 2xl:text-[18px] text-gray-font">
                      {person.seminarPresentation}
                    </p>
                  )}
                </div>
              ) : null}
              {person.additionalInformation?.trim() ? (
                <div className="py-6 [&:first-child]:pt-0">
                  <h3 className={PROFILE_CARD_SUBTITLE_ROBOTO}>
                    Additional Information
                  </h3>
                  {isLikelyHtml(person.additionalInformation) ? (
                    <div
                      className="prose prose-gray max-w-none text-base sm:text-[17px] 2xl:text-[18px] leading-relaxed text-gray-font [&_p]:mb-3 [&_p:last-child]:mb-0"
                      dangerouslySetInnerHTML={{
                        __html: person.additionalInformation,
                      }}
                    />
                  ) : (
                    <p className="whitespace-pre-wrap text-base sm:text-[17px] 2xl:text-[18px] text-gray-font">
                      {person.additionalInformation}
                    </p>
                  )}
                </div>
              ) : null}
            </div>
          ) : canEditProfessionalCareer ? (
            <p className="text-sm text-gray-400 md:text-base">
              No professional career details yet. Use Edit Professional Career
              to add accomplishments, affiliations, activities, and seminars.
            </p>
          ) : null}
        </ProfileOverviewSection>
      ) : null}

      {publicationsText || canEditProfessionalCareer ? (
        <ProfileOverviewSection
          variant={brand.sectionVariant}
          title="Articles"
          defaultOpen
          headerActions={
            canEditProfessionalCareer ? (
              <button
                type="button"
                onClick={() => setProfessionalCareerModalOpen(true)}
                className="inline-flex max-w-full items-center gap-2 whitespace-nowrap font-decimal text-sm font-normal text-white hover:opacity-95 md:text-base"
                aria-label="Edit Articles"
              >
                <span>Edit Articles</span>
                <RedPencil className="h-5 w-5 shrink-0 text-white" />
              </button>
            ) : null
          }
        >
          {publicationsText ? (
            <div
              className={`prose prose-gray max-w-none ${PROFILE_RICH_TEXT_BOLD_ROBOTO}`}
            >
              {isLikelyHtml(publicationsText) ? (
                <div
                  className="text-gray-font text-base sm:text-[17px] 2xl:text-[18px] leading-relaxed [&_p]:mb-3 [&_p:last-child]:mb-0"
                  dangerouslySetInnerHTML={{ __html: publicationsText }}
                />
              ) : (
                <p className="text-gray-font text-base sm:text-[17px] 2xl:text-[18px] leading-relaxed whitespace-pre-wrap">
                  {publicationsText}
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-400 md:text-base">
              No articles yet. Use Edit Articles to add text in the Articles
              field (Professional Career).
            </p>
          )}
        </ProfileOverviewSection>
      ) : null}

      {blogs.length > 0 || canEditGeneralProfile ? (
        <ProfileOverviewSection
          variant={brand.sectionVariant}
          title="Blog"
          defaultOpen
          headerActions={
            canEditGeneralProfile ? (
              <a
                href={MANAGE_BLOGROLLS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex max-w-full items-center gap-2 whitespace-nowrap font-decimal text-sm font-normal text-white hover:opacity-95 md:text-base"
                aria-label="Edit blog (opens in a new tab)"
              >
                <span>Edit Blog</span>
                <RedPencil className="h-5 w-5 shrink-0 text-white" />
              </a>
            ) : null
          }
        >
          {blogs.length > 0 ? (
            <div className="flex flex-col gap-6">
              {blogs.map((blog) => (
                <button
                  key={blog.id ?? blog.slug}
                  type="button"
                  className="flex flex-col text-left"
                  onClick={() =>
                    window.open(
                      `${LEGACY_MEMBER_URL}/blog/post/${blog.slug}.aspx`,
                      "_blank",
                      "noopener,noreferrer",
                    )
                  }
                >
                  <span
                    className={`text-sm sm:text-[17px] 2xl:text-[18px] font-bold !font-roboto ${brand.linkStrong}`}
                  >
                    {blog.title}
                  </span>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-sm sm:text-[17px] 2xl:text-[18px] text-gray-font">
                    <span className="text-base sm:text-[17px] 2xl:text-[18px] text-gray-font">
                      By {blog.author}
                    </span>
                    <span className="text-gray-300">|</span>
                    <span className="text-base sm:text-[17px] 2xl:text-[18px] text-gray-font">
                      {blog.dateCreated
                        ? new Date(blog.dateCreated).toLocaleDateString(
                            "en-US",
                            {
                              year: "numeric",
                              month: "long",
                            },
                          )
                        : ""}
                    </span>
                  </div>
                  {blog.postContent ? (
                    <p
                      className="mt-2 text-gray-font line-clamp-3 text-base sm:text-[17px] 2xl:text-[18px] leading-relaxed"
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {blog.postContent.replace(/<[^>]+>/g, "")}
                    </p>
                  ) : null}
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 md:text-base">
              No blog posts listed yet. Use Edit Blog to manage blogrolls on the
              member site.
            </p>
          )}
        </ProfileOverviewSection>
      ) : null}

      {editFormAnchorId ? (
        <div className="flex justify-center mt-6 px-2">
          <a href={`#${editFormAnchorId}`} className={brand.linkFooter}>
            Update profile details
          </a>
        </div>
      ) : null}

      {canEditGeneralProfile ? (
        <EditGeneralProfileModal
          isOpen={generalProfileModalOpen}
          onClose={() => setGeneralProfileModalOpen(false)}
          person={person}
          languageCatalog={languageCatalog}
          onLanguageAddedToCatalog={handleLanguageAddedToCatalog}
          onSaved={() => {
            onGeneralProfileUpdated?.();
          }}
        />
      ) : null}

      {canEditAreasOfPractice ? (
        <EditAreasOfPracticeModal
          isOpen={aopModalOpen}
          onClose={() => setAopModalOpen(false)}
          person={person}
          onSaved={() => {
            onAreasOfPracticeUpdated?.();
          }}
        />
      ) : null}

      {canEditProfessionalCareer ? (
        <EditProfessionalCareerModal
          isOpen={professionalCareerModalOpen}
          onClose={() => setProfessionalCareerModalOpen(false)}
          person={person}
          onSaved={() => {
            onProfessionalCareerUpdated?.();
          }}
        />
      ) : null}

      {listMoreModal ? (
        <ProfileListMoreModal
          isOpen
          onClose={() => setListMoreModal(null)}
          title={
            listMoreModal === "expertise"
              ? "Practice Expertise"
              : listMoreModal === "areas"
                ? "Areas of Practice"
                : listMoreModal === "industries"
                  ? "WSG Practice Industries"
                  : "WSG Leadership"
          }
          titleId={`profile-list-more-${listMoreModal}`}
        >
          {listMoreModal === "expertise" ? (
            <ul className="w-full list-inside list-disc space-y-2 text-base text-gray-font marker:text-gray-font">
              {displayExpertiseList.map((aop) => (
                <li key={aop.id} className="pl-0.5">
                  {aop.name}
                </li>
              ))}
            </ul>
          ) : null}
          {listMoreModal === "areas" ? (
            <ul className="w-full list-inside list-disc space-y-2 text-base text-gray-font marker:text-gray-font">
              {displayAllAopsList.map((a) => (
                <li key={a.id} className="pl-0.5">
                  {a.name}
                </li>
              ))}
            </ul>
          ) : null}
          {listMoreModal === "industries" ? (
            <div className="flex flex-col gap-3">
              {displayPracticeIndustriesSorted.map((pi) => (
                <div key={pi.id} className="flex min-w-0 items-center gap-3">
                  <div className="h-6 w-6 shrink-0">
                    <SvgIcon practiceId={pi.id} iconUrl={pi.icon} />
                  </div>
                  <span className="text-base text-gray-font">{pi.name}</span>
                </div>
              ))}
            </div>
          ) : null}
          {listMoreModal === "leadership" ? (
            <ul className={`${WSG_LEADERSHIP_LIST_CLASS} space-y-2`}>
              {leadership.map((entry) => (
                <li key={entry.key} className={WSG_LEADERSHIP_ITEM_CLASS}>
                  {entry.label}
                </li>
              ))}
            </ul>
          ) : null}
        </ProfileListMoreModal>
      ) : null}
    </div>
  );
};

export default ProfileEditModeOverview;
